import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  InputAdornment, 
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Google as GoogleIcon } from '@mui/icons-material';
import MicrosoftIcon from '@mui/icons-material/Window';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { googleProvider, microsoftProvider } from '../../firebase';
import { COLORS, FONTS } from '../../theme/tokens';
import {
  FORM_FIELD_SX,
  PANEL_SX,
  PRIMARY_BUTTON_SX,
  QUIET_LINK_SX,
  SECONDARY_BUTTON_SX,
} from '../../theme/v2Styles';
const Login = ({ switchToRegister }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { email, password } = formData;
  // Use the custom hook for cleaner access
  const { login, loginWithFirebase, isAuthenticated, error, clearErrors, loading } = useAuth();
  const navigate = useNavigate();
  // Effect to handle redirection after authentication changes
  useEffect(() => {
    // Clear errors when the component mounts or when navigating away
    clearErrors();
    return () => {
      clearErrors(); // Clear errors on unmount as well
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount/unmount for error clearing

  useEffect(() => {
    // This effect runs when isAuthenticated or loading state changes
    if (isAuthenticated && !loading) {
      //console.log("Login component detected authenticated state, redirecting...");
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      localStorage.removeItem('redirectAfterLogin'); // Clear it immediately
      
      // Use navigate for SPA navigation, fallback to window.location if needed
      navigate(redirectUrl || '/dashboard', { replace: true });
      // Using { replace: true } prevents the login page from being in the history
      
      // As a fallback if navigate doesn't work reliably in all cases after async ops
      // setTimeout(() => {
      //   if (window.location.pathname === '/login') { // Check if still on login page
      //      window.location.href = redirectUrl || '/dashboard';
      //   }
      // }, 100);
    }
  }, [isAuthenticated, loading, navigate]); // Add dependencies
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear field-specific error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: null });
    }
  };
  const validateForm = () => {
    const errors = {};
    if (!email) {
      errors.email = t('auth.errEmailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('auth.errEmailInvalid');
    }
    if (!password) {
      errors.password = t('auth.errPasswordRequired');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        // Direct call to login, store the result
        await login(formData);
      } catch (err) {
        
      }
    }
  };
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleFirebaseLogin = async (provider) => {
    // Clear previous errors before attempting social login
    clearErrors();
    const success = await loginWithFirebase(provider);
  };

  return (
    <Box
      data-testid="login-v2-layout"
      sx={{
        ...PANEL_SX,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
        maxWidth: 1040,
        mx: 'auto',
        my: { xs: 1, md: 3 },
        overflow: 'hidden',
        minHeight: { md: 620 },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-end',
          p: 4,
          color: '#FFFFFF',
          backgroundImage: 'linear-gradient(to top, rgba(20,28,23,0.68), rgba(20,28,23,0.04)), url(https://storage.googleapis.com/eladore-ruffles.appspot.com/images/full/c8152c5ccf58112719a5c06af88f2d30.jpg.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box>
          <Typography sx={{ fontFamily: FONTS.heading, fontStyle: 'italic', fontSize: '1.125rem' }}>
            {t('auth.tagline')}
          </Typography>
          <Typography sx={{ mt: 1, color: COLORS.goldLight, fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase' }}>
            {t('auth.atelier')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: { xs: 3, sm: 5, md: 7 }, py: { xs: 4, md: 7 }, alignSelf: 'center' }}>
        <Typography component="h1" sx={{ fontFamily: FONTS.heading, fontWeight: 600, fontSize: { xs: '1.625rem', md: '1.875rem' }, color: COLORS.textPrimary, mb: 1 }}>
          {t('auth.welcomeBack')}
        </Typography>
        <Typography sx={{ color: COLORS.textSecondary, mb: 3.5 }}>
          {t('auth.loginIntro')}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
        <TextField
          label={t('auth.email')}
          type="email"
          name="email"
          value={email}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!formErrors.email}
          helperText={formErrors.email}
          required
          autoComplete="email"
          sx={FORM_FIELD_SX}
        />
        <TextField
          label={t('auth.password')}
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={password}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!formErrors.password}
          helperText={formErrors.password}
          required
          autoComplete="current-password"
          sx={FORM_FIELD_SX}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={toggleShowPassword}
                  edge="end"
                  aria-label={t('auth.togglePassword')}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Button 
          type="submit" 
          variant="contained" 
          color="primary" 
          fullWidth 
          sx={{ ...PRIMARY_BUTTON_SX, mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('auth.login')}
        </Button>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2">
            {t('auth.noAccount')}{' '}
            <Button 
              onClick={switchToRegister} 
              color="primary" 
              sx={{ ...QUIET_LINK_SX, textTransform: 'none', p: 0, ml: 1 }}
            >
              {t('auth.registerHere')}
            </Button>
          </Typography>
        </Box>
      </Box>

      {/* Divider and Social Logins */}
        <Divider sx={{ my: 3, color: COLORS.textFaint, fontSize: '0.6875rem', letterSpacing: '1px' }}>{t('auth.or')}</Divider>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<GoogleIcon />}
          fullWidth
          onClick={() => handleFirebaseLogin(googleProvider)}
          disabled={loading}
          sx={{ ...SECONDARY_BUTTON_SX, justifyContent: 'center', color: COLORS.textPrimary, borderColor: COLORS.borderControl }}
        >
          {t('auth.signInGoogle')}
        </Button>
        {/* Facebook login temporarily hidden - uncomment when Facebook OAuth is fixed
        <Button
          variant="outlined"
          startIcon={<FacebookIcon />}
          fullWidth
          onClick={() => handleFirebaseLogin(facebookProvider)}
          disabled={loading}
          sx={{ 
            justifyContent: 'center',
            color: '#1877F2', // Facebook blue
            borderColor: '#1877F2',
            '&:hover': {
              borderColor: '#1877F2',
              backgroundColor: 'rgba(24, 119, 242, 0.04)'
            }
          }}
        >
          {t('auth.signInFacebook')}
        </Button> */}
        <Button
          variant="outlined"
          startIcon={<MicrosoftIcon />}
          fullWidth
          onClick={() => handleFirebaseLogin(microsoftProvider)}
          disabled={loading}
          sx={{ ...SECONDARY_BUTTON_SX, justifyContent: 'center', color: COLORS.textPrimary, borderColor: COLORS.borderControl }}
        >
          {t('auth.signInMicrosoft')}
        </Button>
        {/* Apple Sign in Button Removed */}
        </Box>
      </Box>
    </Box>
  );
};
export default Login;
